import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
    FileText,
    Plus,
    Clock,
    User,
    ExternalLink,
    AlertTriangle,
    CheckCircle2,
    MessageSquare,
    ArrowUpRight
} from 'lucide-react';
import { cn } from '../lib/utils';
import { StatusBadge } from '../components/ui/StatusBadge';

import { uploadFile } from '../services/upload';

const Procurement: React.FC = () => {
    const [procs, setProcs] = useState<any[]>([]);
    const [activeProcId, setActiveProcId] = useState<string | null>(null);
    const [amount, setAmount] = useState('');
    const [days, setDays] = useState('');
    const [remarks, setRemarks] = useState('');
    const [attachmentUrl, setAttachmentUrl] = useState('');
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        api.get('/procurements').then(res => setProcs(res.data));
    }, []);

    const handleSubmit = async (procId: string) => {
        try {
            await api.post('/quotations', {
                procurement_id: procId,
                amount: parseFloat(amount),
                timeline_days: parseInt(days),
                remarks,
                attachment_url: attachmentUrl || null
            });
            alert('Quotation submitted successfully!');
            setAmount(''); setDays(''); setRemarks(''); setAttachmentUrl('');
            setActiveProcId(null);
            api.get('/procurements').then(res => setProcs(res.data));
        } catch (e) { alert('Submission failed'); }
    };

    const flagQuote = async (quoteId: string) => {
        const reason = window.prompt('Reason for flagging (e.g. Conflict of Interest):');
        if (!reason) return;
        try {
            await api.post(`/quotations/${quoteId}/flag`, { reason });
            alert('Quotation flagged for board review.');
        } catch (e) { alert('Action failed'); }
    };

    return (
        <div className="space-y-8 max-w-4xl mx-auto pb-10">
            <div>
                <h1 className="text-3xl font-bold text-slate-900">Procurement & Tenders</h1>
                <p className="text-slate-500 mt-1">Transparent bidding for society projects and maintenance</p>
            </div>

            <div className="space-y-6">
                {procs.map((p: any) => (
                    <div key={p.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                        <div className="p-6 border-b border-slate-100 flex flex-col md:flex-row justify-between gap-6">
                            <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                    <StatusBadge status={p.status} />
                                    <span className="text-[10px] text-slate-400 font-bold uppercase flex items-center gap-1">
                                        <Clock size={12} /> Deadline: {new Date(p.deadline).toLocaleDateString()}
                                    </span>
                                </div>
                                <h2 className="text-xl font-bold text-slate-900">{p.title}</h2>
                                <p className="text-sm text-slate-500 mt-2 leading-relaxed">{p.description}</p>
                            </div>

                            <button
                                onClick={() => setActiveProcId(activeProcId === p.id ? null : p.id)}
                                className="bg-slate-900 text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-slate-800 transition-all flex items-center gap-2 h-fit"
                            >
                                {activeProcId === p.id ? 'Cancel' : 'Submit Counter-Quote'}
                                <Plus size={18} className={cn("transition-transform", activeProcId === p.id && "rotate-45")} />
                            </button>
                        </div>

                        {activeProcId === p.id && (
                            <div className="p-6 bg-violet-50/50 border-b border-violet-100 animate-in slide-in-from-top-2">
                                <h3 className="text-xs font-bold text-violet-900 uppercase tracking-widest mb-4 flex items-center gap-2">
                                    <MessageSquare size={14} /> Quote Details
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-slate-500 uppercase">Amount (₹)</label>
                                        <input type="number" value={amount} onChange={e => setAmount(e.target.value)} className="w-full bg-white border border-violet-200 rounded-lg p-2.5 text-sm font-bold outline-none focus:ring-2 focus:ring-violet-500" required />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-slate-500 uppercase">Timeline (Days)</label>
                                        <input type="number" value={days} onChange={e => setDays(e.target.value)} className="w-full bg-white border border-violet-200 rounded-lg p-2.5 text-sm font-bold outline-none focus:ring-2 focus:ring-violet-500" required />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-slate-500 uppercase">Remarks</label>
                                        <input type="text" value={remarks} onChange={e => setRemarks(e.target.value)} className="w-full bg-white border border-violet-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" placeholder="Why this vendor?" required />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-slate-500 uppercase">Attachment (PDF)</label>
                                        {attachmentUrl ? (
                                            <div className="w-full bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg p-2 text-xs font-bold flex items-center justify-center gap-1 h-[42px]">
                                                <CheckCircle2 size={16} /> PDF Attached
                                            </div>
                                        ) : (
                                            <label className="cursor-pointer bg-white border border-violet-200 hover:bg-violet-50 text-violet-600 rounded-lg p-2.5 text-xs font-bold flex items-center justify-center gap-1.5 h-[42px] transition-all">
                                                <Plus size={16} /> {uploading ? 'Uploading...' : 'Attach PDF'}
                                                <input
                                                    type="file"
                                                    accept=".pdf,image/*"
                                                    className="hidden"
                                                    disabled={uploading}
                                                    onChange={async (e) => {
                                                        const file = e.target.files?.[0];
                                                        if (file) {
                                                            try {
                                                                setUploading(true);
                                                                const url = await uploadFile(file, 'quotations');
                                                                setAttachmentUrl(url);
                                                            } catch (err) {
                                                                alert('Failed to upload PDF');
                                                            } finally {
                                                                setUploading(false);
                                                            }
                                                        }
                                                    }}
                                                />
                                            </label>
                                        )}
                                    </div>
                                </div>
                                <div className="mt-4 flex justify-end">
                                    <button onClick={() => handleSubmit(p.id)} className="bg-violet-600 text-white px-6 py-2 rounded-lg font-bold text-sm hover:bg-violet-700">Submit to Board</button>
                                </div>
                            </div>
                        )}

                        <div className="p-6">
                            <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">Current Quotations ({p.quotations.length})</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {p.quotations.map((q: any) => (
                                    <div key={q.id} className={cn(
                                        "p-4 rounded-xl border relative transition-all group/card",
                                        q.is_admin_quote ? "bg-slate-50 border-slate-200" : "bg-white border-violet-100 shadow-sm"
                                    )}>
                                        <div className="flex justify-between items-start mb-2">
                                            <span className="text-lg font-bold text-slate-900">₹{parseFloat(q.amount).toLocaleString()}</span>
                                            <span className="text-[10px] font-bold text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded uppercase">{q.timeline_days}d</span>
                                        </div>
                                        <p className="text-xs text-slate-500 italic mb-4 line-clamp-2">"{q.remarks || 'No remarks'}"</p>
                                        <div className="flex items-center justify-between mt-auto pt-3 border-t border-slate-100">
                                            <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-400 uppercase">
                                                <User size={12} /> {q.is_admin_quote ? 'Board Vendor' : 'Resident Suggest'}
                                            </div>
                                            <div className="flex gap-2">
                                                {!q.is_admin_quote && (
                                                    <button onClick={() => flagQuote(q.id)} className="text-slate-300 hover:text-red-500" title="Flag Conflict">
                                                        <AlertTriangle size={14} />
                                                    </button>
                                                )}
                                                {q.attachment_url && (
                                                    <a href={q.attachment_url} target="_blank" rel="noreferrer" className="text-violet-500 hover:text-violet-700">
                                                        <ArrowUpRight size={16} />
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ))}
                {procs.length === 0 && (
                    <div className="py-20 text-center bg-white rounded-2xl border border-dashed border-slate-200">
                        <FileText size={40} className="text-slate-200 mx-auto mb-3" />
                        <p className="text-slate-500 font-medium">No active procurement requirements</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Procurement;
